import { defineStore } from 'pinia';
import { notify } from 'notiwind';
import { handleError } from '@/utils/error-handler';

export const useMembersStore = defineStore('members', {
    state: () => ({
        loadingMembers: false,
        showChallengeModal: false,
        showConnectModal: false,
        members: [],
        membersByUser: {}, // New state property to store members organized by user
        sharedUsers: [],
        walletId: 0,
        banks: [],
        member: {},
        connectType: '',
        users: [],
        wallet: {},
    }),
    actions: {
        /**
         * Group members by the wallet user that owns each connection.
         *
         * A wallet's connections normally all share a single MX login
         * (`user_guid`) but span multiple wallet users (P1, Business Entity 1,
         * …). Grouping by `user_guid` collapsed them into a single bucket;
         * SUPPORT-628 requires one bucket per real wallet user instead.
         *
         * @param {Array} members - Array of member objects to process
         * @param {Object} membersByUser - Existing membersByUser object to add to
         * @returns {Object} Updated membersByUser object
         */
        processMembersByUser(members, membersByUser = {}) {
            if (!members || !members.length) return membersByUser;

            members.forEach((member) => {
                const walletUser =
                    member.wallet_user && member.wallet_user !== false
                        ? member.wallet_user
                        : null;
                const groupKey = walletUser
                    ? `wu:${walletUser.uuid ?? walletUser.id}`
                    : 'unknown';
                if (!membersByUser[groupKey]) {
                    membersByUser[groupKey] = {
                        user: walletUser,
                        members: [],
                    };
                }
                membersByUser[groupKey].members.push(member);
            });

            return membersByUser;
        },

        getMembers(uuid = null) {
            this.loadingMembers = true;
            ApiService.apiCall('/wallets/members?uuid=' + uuid, 'get')
                .then((response) => {
                    this.walletId = response.data.wallet.id;
                    this.members = response.data.members;
                    this.sharedUsers = response.data.shared_users;
                    this.banks = response.data.banks;
                    this.users = response.data.wallet.users;
                    this.wallet = response.data.wallet;

                    // Organize members by user_guid
                    let membersByUser = {};

                    // Process regular members
                    membersByUser = this.processMembersByUser(
                        this.members,
                        membersByUser
                    );

                    // Process shared users' members. Each entry is a
                    // { user, members } group, so we pass its members array
                    // (passing the wrapper object skipped them entirely — SUPPORT-626).
                    if (this.sharedUsers && this.sharedUsers.length) {
                        this.sharedUsers.forEach((sharedUser) => {
                            membersByUser = this.processMembersByUser(
                                sharedUser.members,
                                membersByUser
                            );
                        });
                    }

                    this.membersByUser = membersByUser;

                    setTimeout(() => {
                        this.loadingMembers = false;
                    }, 500);
                })
                .catch((response) => {
                    handleError(response);
                    this.loadingMembers = false;
                });
        },
        deleteMember(guid) {
            return new Promise((resolve, reject) => {
                ApiService.apiCall(
                    '/wallets/' + this.walletId + '/members/' + guid,
                    'delete'
                )
                    .then((response) => {
                        this.getMembers();
                        resolve(response);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        setShowChallengeModal(val, member) {
            this.member = member;
            this.showChallengeModal = val;
        },
        setShowConnectModal(val, member, type) {
            this.member = member;
            this.connectType = type;
            this.showConnectModal = val;
        },
        /**
         * Update a member's nickname in the store (optimistic update)
         * @param {string} memberGuid - The member GUID to update
         * @param {string|null} nickname - The new nickname value
         */
        updateMemberNickname(memberGuid, nickname) {
            // Update in members array
            const memberIndex = this.members.findIndex(
                (m) => m.guid === memberGuid
            );
            if (memberIndex !== -1) {
                const member = this.members[memberIndex];
                if (!member.connect_attempt) {
                    member.connect_attempt = {};
                }
                member.connect_attempt.connection_nickname = nickname;
            }

            // Update in membersByUser
            Object.values(this.membersByUser).forEach((userGroup) => {
                const member = userGroup.members.find(
                    (m) => m.guid === memberGuid
                );
                if (member) {
                    if (!member.connect_attempt) {
                        member.connect_attempt = {};
                    }
                    member.connect_attempt.connection_nickname = nickname;
                }
            });
        },
    },
});
